import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, deleteDoc, doc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";

const COLLECTION = "smsLogs";

interface LogRow {
  id: string;
  tarih: Timestamp | null;
  alici: string;
  aliciAd: string;
  mesaj: string;
  durum: "gönderildi" | "hata";
}

function formatTarih(t: Timestamp | null): string {
  if (!t || typeof t.toDate !== "function") return "—";
  try {
    return t.toDate().toLocaleString("tr-TR");
  } catch {
    return "—";
  }
}

const SmsLogPage: React.FC = () => {
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [filter, setFilter] = useState<string>("hepsi");
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    setLoading(true);
    setHata(false);
    try {
      const snap = await getDocs(query(collection(db, COLLECTION), orderBy("tarih", "desc")));
      setLogs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as LogRow[]);
    } catch (e) {
      console.error("SMS geçmişi okunamadı:", e);
      setHata(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = logs.filter((l) => filter === "hepsi" || l.durum === filter);

  const handleClear = async () => {
    if (!logs.length) return;
    if (!window.confirm("Tüm SMS geçmişi silinsin mi? Bu işlem geri alınamaz.")) return;
    setClearing(true);
    try {
      // Toplu silme — her dökümanı tek tek sil.
      await Promise.all(logs.map((l) => deleteDoc(doc(db, COLLECTION, l.id))));
      await load();
    } catch (e) {
      console.error("SMS geçmişi temizlenemedi:", e);
      window.alert("Geçmiş temizlenemedi, lütfen tekrar deneyin.");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">SMS Geçmişi</h2>
          <p className="text-sm text-gray-500 mt-0.5">{loading ? "Yükleniyor..." : `${logs.length} kayıt`}</p>
        </div>
        <div className="flex gap-2">
          {["hepsi", "gönderildi", "hata"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors
                ${filter === f ? "bg-[#8B1428] text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
              {f === "hepsi" ? "Hepsi" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          {logs.length > 0 && (
            <button onClick={handleClear} disabled={clearing}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-red-500 border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50">
              <i className="fa-solid fa-trash mr-1" /> {clearing ? "Siliniyor..." : "Temizle"}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400"><i className="fa-solid fa-circle-notch fa-spin text-3xl" /></div>
      ) : hata ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">
          Geçmiş yüklenemedi. <button onClick={load} className="underline font-semibold">Tekrar dene</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-clock-rotate-left text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500">Henüz SMS gönderilmedi</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tarih</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Alıcı</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Mesaj</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Durum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{formatTarih(log.tarih)}</td>
                  <td className="px-4 py-3 text-gray-700 text-xs">{log.aliciAd} ({log.alici})</td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate hidden md:table-cell">{log.mesaj}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full
                      ${log.durum === "gönderildi" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                      {log.durum === "gönderildi"
                        ? <><i className="fa-solid fa-check mr-1" />Gönderildi</>
                        : <><i className="fa-solid fa-xmark mr-1" />Hata</>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            {filtered.length} kayıt gösteriliyor
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsLogPage;
